import React from 'react'
import { Building2, ChevronLeft, Settings, Package, Container, Truck, CheckSquare } from 'lucide-react'
import { Button, cn } from '@wove/web-ui'
import { useNavigate, Link, useLocation, Outlet, useParams } from 'react-router-dom'

interface SettingsTab {
  id: string
  label: string
  icon: React.ReactNode
  href: string
}

const getTabsForContext = (integrationId?: string): SettingsTab[] => {
  if (integrationId) {
    // Integration detail page - no sidebar tabs needed since tabs are in the main content
    return []
  }
  
  // Organization-wide tabs
  return [
    {
      id: 'overview',
      label: 'Overview',
      icon: <Building2 className="h-4 w-4" />,
      href: '/organization-settings',
    },
    {
      id: 'cargowise-reference',
      label: 'CargoWise Reference',
      icon: <Truck className="h-4 w-4" />,
      href: '/organization-settings/cargowise-reference',
    },
    {
      id: 'validation-rules',
      label: 'Validation Rules',
      icon: <CheckSquare className="h-4 w-4" />,
      href: '/organization-settings/validation-rules',
    },
  ]
}

export default function OrganizationSettingsLayout() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { integrationId } = useParams<{ integrationId: string }>()
  
  const tabs = getTabsForContext(integrationId)
  const isIntegrationContext = !!integrationId

  return (
    <div className="h-screen flex flex-col">
      <div className="flex items-center p-3">
        <Button
          variant="ghost"
          size="icon"
          className="mr-2"
          onClick={() => navigate(isIntegrationContext ? '/organization-settings' : '/')}
          aria-label={isIntegrationContext ? "Back to organization settings" : "Back to dashboard"}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Building2 className="h-6 w-6 mr-2 text-blue-600" />
        <h1 className="text-3xl font-bold tracking-tight">
          {isIntegrationContext ? `Integration Settings (${integrationId})` : 'Organization Settings'}
        </h1>
      </div>

      <div className="flex flex-col md:flex-row gap-8 flex-1 min-h-0">
        {!isIntegrationContext && tabs.length > 0 && (
          <aside className="md:w-64 shrink-0">
            <div className="flex h-full flex-col gap-2 p-4">
              <nav className="grid gap-1 py-2">
                {tabs.map((tab) => (
                  <Link
                    key={tab.id}
                    to={tab.href}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-all hover:text-foreground',
                      pathname === tab.href && 'bg-muted font-medium text-foreground',
                    )}
                  >
                    {tab.icon}
                    {tab.label}
                  </Link>
                ))}
              </nav>
            </div>
          </aside>
        )}

        <div className={cn("flex-1 overflow-auto", !isIntegrationContext && tabs.length > 0 && "pr-4")}>
          <Outlet />
        </div>
      </div>
    </div>
  )
}